import { ref, computed, onMounted } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { FetchPackageCalenderResponse, PackageSelectDay } from '@dto';
import { AxiosAdapter, PackageAdapter } from '@adapters';
import { padStringStart } from '@core/utils';
import { PackageTgResveTime } from '@core/dto';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface FetchCalendarReservationListPayload {
  selectDay?: string;
  bsnCode?: string;
}

export const useReservationPackageCalender = (
  emit: any,
  year: number,
  month: number,
  type: string
) => {
  const axiosAdapter = new AxiosAdapter();
  const packageAdapter = new PackageAdapter(axiosAdapter);

  const monthOffset = ref(0);
  const packageCalenderList = ref<FetchPackageCalenderResponse[] | []>([]);
  const childSelectDay = ref<string>('');
  const isReady = ref<boolean>(false);
  const axiosTry = ref<boolean>(false);

  const nowDate = ref(dayjs(new Date(year, month - 1, 1)));

  const baseCalendar = computed(() =>
    dayjs(nowDate.value).add(monthOffset.value, 'months').toDate()
  );
  const nextCalendar = computed(() =>
    dayjs(nowDate.value)
      .add(monthOffset.value + 1, 'months')
      .toDate()
  );
  const _fetchCalendarData = async ({
    selectDay,
    packageDwId,
  }: FetchCalendarReservationListPayload & { packageDwId?: number }) => {
    const data = await packageAdapter.fetchCalenderPackageList({
      year: baseCalendar.value.getFullYear(),
      month: baseCalendar.value.getMonth() + 1,
      bsnDate: selectDay,
      packageDwId,
      roomType: type,
    });
    return data;
  };

  /**
   * 달력 데이터를 가져온 후 동작하는 함수
   *
   * emit으로 상위컴포넌트에 데이터 전달
   * 당일예약 상세정보가 있는 경우 openTime
   * 당일예약 상세정보가 없는 경우 closeTime
   *
   */
  const _handleCalendarReservationListComplete = ({
    selectDay,
    bsnCode,
    availableDay,
  }: PackageSelectDay = {}) => {
    console.log(selectDay, bsnCode, availableDay);
    isReady.value = true;

    if (axiosTry.value) {
      const startDate = dayjs(selectDay);
      const endDate = startDate.add(
        availableDay && Object.keys(availableDay).length
          ? Object.keys(availableDay).length - 1
          : 0,
        'day'
      );
      const result = packageCalenderList.value
        .filter((item) => {
          const itemDate = dayjs(item.bsnDate);
          return (
            itemDate.isSameOrAfter(startDate) &&
            itemDate.isSameOrBefore(endDate)
          );
        })
        .map((data) =>
          data?.tgResveTimes?.length
            ? data.tgResveTimes.map((m) => ({ ...m, select: false }))
            : []
        );
      const emitAction = result.every(
        (e: PackageTgResveTime[] | []) => !e.length
      )
        ? 'closeTime'
        : 'openTime';
      emitAction === 'openTime'
        ? emit(emitAction, {
            result,
            availableDay,
            date: selectDay,
            bsnCode,
          })
        : emit(emitAction);
    } else {
      emit('closeTime');
    }
  };

  const _updateState = (calenders: FetchPackageCalenderResponse[]) => {
    packageCalenderList.value = calenders;
  };

  const _fetchCalendarReservationList = async (
    payload: PackageSelectDay = {}
  ) => {
    try {
      const data = await _fetchCalendarData({
        selectDay: payload.selectDay,
        packageDwId: payload.packageDwId,
      });
      axiosTry.value = true;
      _updateState(data);
    } catch (e) {
      childSelectDay.value = '';
      axiosTry.value = false;
    } finally {
      _handleCalendarReservationListComplete(payload);
    }
  };

  const onSelectDay = async ({
    bsnCode = '',
    selectDay,
    packageDwId,
    availableDay,
  }: PackageSelectDay = {}) => {
    if (selectDay != null) {
      childSelectDay.value = selectDay;
    }
    await _fetchCalendarReservationList({
      selectDay,
      bsnCode,
      packageDwId,
      availableDay,
    });
  };

  const changeMonth = async (param: string) => {
    childSelectDay.value = '';
    param === 'next' ? monthOffset.value++ : monthOffset.value--;
    await _fetchCalendarReservationList();
  };

  onMounted(async () => {
    /**
     * 메인화면에서 달력 클릭해서 들어온 경우
     * 해당 날짜 바로 선택
     */

    await _fetchCalendarReservationList();
  });

  return {
    nextCalendar,
    baseCalendar,
    packageCalenderList,
    childSelectDay,
    isReady,
    changeMonth,
    onSelectDay,
    padStringStart,
  };
};
